import { motion } from 'framer-motion';
import { AdvancedCalculator } from '../components/AdvancedCalculator';
import {ChevronDown, Star, Compass, BookOpen} from 'lucide-react';
import { ReactNode } from 'react';

export function Home() {
  return (
    <div className="space-y-20 pb-20">
      {/* Affiliate Banner */}
      <div className="w-full bg-gradient-to-r from-[#0F1219] via-[#1a1508] to-[#0F1219] border-b border-[#D4AF37]/20">
        <a
          href="https://hop.clickbank.net/?affiliate=ascendcalc&vendor=astrolan&cbpage=vsl"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-center gap-3 px-4 py-3 group"
        >
          <Star className="w-4 h-4 text-[#D4AF37] fill-current flex-shrink-0" />
          <span className="text-sm md:text-base text-gray-300 group-hover:text-white transition-colors text-center">
            ✨ <span className="text-[#D4AF37] font-semibold">Discover Your Cosmic Destiny</span> — Unlock a Personalized Astrology Reading Today
          </span>
          <span className="hidden md:inline-flex items-center gap-1 px-3 py-1 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/40 text-[#D4AF37] text-xs font-medium group-hover:bg-[#D4AF37]/20 transition-all flex-shrink-0">
            Get Reading →
          </span>
          <Star className="w-4 h-4 text-[#D4AF37] fill-current flex-shrink-0" />
        </a>
      </div>

      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden">
        {/* Background Image Placeholder - Replace with generated image */}
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-[#0B0E14]/60 z-10" />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#0B0E14]/50 to-[#0B0E14] z-10" />
          <img 
            src="https://static.lumi.new/93/93d072343ff66927579f36dbd52c3c48.png" 
            alt="Mystical Zodiac Wheel" 
            className="w-full h-full object-cover opacity-60"
          />
        </div>

        <div className="relative z-20 max-w-7xl mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="inline-block mb-4 px-4 py-1 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/30 text-[#D4AF37] text-sm font-medium tracking-wide">
              Discover Your Celestial Blueprint
            </div>
            <h1 className="text-5xl md:text-7xl font-serif font-bold text-white mb-6 leading-tight">
              Find Your <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#D4AF37] to-[#FCD34D]">Rising Sign</span><br />
              & Unlock Your Destiny
            </h1>
            <p className="text-xl text-gray-300 max-w-2xl mx-auto mb-10 leading-relaxed">
              Your Rising Sign is the mask you wear and the first impression you make. 
              Calculate your accurate Rising Sign Astro chart alongside your Sun and Moon signs instantly.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.4, duration: 0.8 }}
          >
            <AdvancedCalculator />
          </motion.div>
        </div>

        <motion.div 
          animate={{ y: [0, 10, 0] }}
          transition={{ repeat: Infinity, duration: 2 }}
          className="absolute bottom-10 left-1/2 -translate-x-1/2 text-gray-500 z-20"
        >
          <ChevronDown className="w-8 h-8" />
        </motion.div>
      </section>

      {/* Content Pillars */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <FeatureCard 
            icon={<Compass className="w-8 h-8 text-[#D4AF37]" />}
            title="Rising Sign Astro"
            description="The Ascendant or Rising Sign represents your physical body and outward style. It changes every 2 hours, making it the most personal point in your chart."
          />
          <FeatureCard 
            icon={<Star className="w-8 h-8 text-[#D4AF37]" />}
            title="The Big 3"
            description="Sun (Ego), Moon (Emotion), and Rising (Persona). Understanding these three pillars gives you a complete picture of your astrological identity."
          />
          <FeatureCard 
            icon={<BookOpen className="w-8 h-8 text-[#D4AF37]" />}
            title="Scientific Accuracy"
            description="Our calculators use precise ephemeris data to account for birth location coordinates and time zone adjustments."
          />
        </div>
      </section>

      {/* SEO Content Section */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <h2 className="text-3xl font-serif font-bold text-white mb-6">Why Your Rising Sign Matters</h2>
        <div className="prose prose-invert prose-lg mx-auto text-gray-400">
          <p>
            While most people know their Sun sign (based on birth day), your <strong>Rising Sign</strong> (or Ascendant) is determined by the exact time and location of your birth. It represents the constellation that was rising on the eastern horizon at the moment you took your first breath.
          </p>
          <p>
            In <strong>Rising Sign Astro</strong>, this placement dictates your chart ruler and sets the structure for your entire house system. It is often considered more important than the Sun sign for predictive astrology.
          </p>
        </div>
      </section>
    </div>
  );
}

function FeatureCard({ icon, title, description }: { icon: ReactNode, title: string, description: string }) {
  return (
    <div className="bg-[#0F1219] p-8 rounded-2xl border border-white/5 hover:border-[#D4AF37]/30 transition-colors group">
      <div className="mb-6 p-4 bg-[#0B0E14] rounded-xl inline-block group-hover:scale-110 transition-transform duration-300">
        {icon}
      </div>
      <h3 className="text-xl font-serif font-bold text-white mb-3">{title}</h3>
      <p className="text-gray-400 leading-relaxed">
        {description}
      </p>
    </div>
  );
}
